import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import EventCard from "@/components/events/EventCard";
import EventCalendar from "@/components/events/EventCalendar";
import AnnouncementsSection, {
  Announcement,
} from "@/components/events/AnnouncementsSection";
import BonusProgramsSection from "@/components/events/BonusProgramsSection";
import { ActiveBonusDTO } from "@/types/bonus";
import { PositionRequestModal } from "@/components/PositionRequestModal";
import {
  CalendarCheck,
  History,
  GraduationCap,
  ArrowRight,
  CalendarX,
  CalendarDays,
  Search,
  X,
} from "lucide-react";
import { format, isSameDay } from "date-fns";
import { FilterTabs } from "@/components/brand/FilterTabs";
import { EmptyState } from "@/components/brand/EmptyState";
import { Loading } from "@/components/ui/loading";
import { useQuery } from "@tanstack/react-query";
import { axiosInstance } from "./auth/auth.context";
import { positionService, Position } from "@/services/positionService";

type EventFilter = "all" | "my-upcoming" | "past";

/** The filters in the order the segmented control shows them. */
const FILTERS: EventFilter[] = ["all", "my-upcoming", "past"];

const FILTER_TABS = [
  { label: "All Upcoming", icon: CalendarCheck },
  { label: "My Events", icon: CalendarCheck },
  { label: "Past Events", icon: History },
];

/**
 * What an empty list says, per filter. Each is empty for a different reason.
 *
 * A failed request lands here too, showing emptiness rather than an error.
 * That is deliberate and long-standing - see SPEC 11.15 - not an oversight.
 */
const EMPTY: Record<
  EventFilter,
  { icon: React.ElementType; title: string; description: string }
> = {
  all: {
    icon: CalendarX,
    title: "No upcoming events",
    description:
      "New events are posted here as they are booked. It is worth checking back.",
  },
  "my-upcoming": {
    icon: CalendarCheck,
    title: "You are not signed up yet",
    description:
      "Events you sign up for will appear here, with the positions you are working.",
  },
  past: {
    icon: History,
    title: "No past events",
    description:
      "Events you have worked will appear here once they have finished.",
  },
};

export interface EventSummary {
  eventId: string;
  companyName: string;
  startTime: string;
  eventLength: number;
  address: string;
  city: string;
  isUserRegistered: boolean;
  userPositions: string[];
  totalPositions: number;
  filledPositions: number;
  userOnWaitlist: boolean;
}

const Index = () => {
  // Undefined, not today: the list honours this date, so defaulting it would
  // open the page with nearly everything hidden.
  const [date, setDate] = useState<Date | undefined>(undefined);
  const [filter, setFilter] = useState<EventFilter>("all");
  const [positionModalOpen, setPositionModalOpen] = useState(false);

  const { data: announcements, isLoading: announcementLoading } = useQuery({
    queryKey: ["announcements"],
    queryFn: async (): Promise<Announcement[]> => {
      const response = await axiosInstance.get<Announcement[]>("announcement");
      return response.data;
    },
  });

  const { data: bonuses, isLoading: bonusLoading } = useQuery({
    queryKey: ["bonuses"],
    queryFn: async (): Promise<ActiveBonusDTO[]> => {
      const response =
        await axiosInstance.get<ActiveBonusDTO[]>("bonus/active");
      return response.data;
    },
  });

  // Fetch available positions to determine if button should be shown
  const { data: availablePositions = [] } = useQuery({
    queryKey: ["positions", "requestable", "check-available"],
    queryFn: (): Promise<Position[]> =>
      positionService.getPositions({
        requestable: true,
        excludeUserSignedOffPositions: true,
        excludeUserRequestedPositions: false,
      }),
  });

  const showPositionButton = availablePositions.length > 0;

  // Separate queries for upcoming and past events
  const { data: upcomingEvents, isLoading: upcomingEventsLoading } = useQuery({
    queryKey: ["events", "upcoming"],
    queryFn: async (): Promise<EventSummary[]> => {
      const response = await axiosInstance.get<EventSummary[]>(
        "userevent/available",
      );
      return response.data;
    },
    enabled: filter !== "past", // Only fetch when not showing past events
  });

  const { data: pastEvents, isLoading: pastEventsLoading } = useQuery({
    queryKey: ["events", "past"],
    queryFn: async (): Promise<EventSummary[]> => {
      const response = await axiosInstance.get<EventSummary[]>(
        "userevent/my-events/past",
      );
      return response.data;
    },
    enabled: filter === "past", // Only fetch when showing past events
  });

  // Determine which events to show based on the filter
  const filteredEvents = (() => {
    if (filter === "past") {
      return pastEvents || [];
    }

    const events = upcomingEvents || [];
    const now = new Date();

    if (filter === "my-upcoming") {
      return events.filter(
        (event) => new Date(event.startTime) >= now && event.isUserRegistered,
      );
    }

    // "all" filter - show all upcoming events
    return events;
  })();

  // The picked day narrows what the tab is showing rather than replacing it.
  // Nothing is refetched; the calendar is drawn from these same rows.
  const visibleEvents = date
    ? filteredEvents.filter((event) => {
        const start = new Date(event.startTime);
        return !isNaN(start.getTime()) && isSameDay(start, date);
      })
    : filteredEvents;

  const isLoading =
    filter === "past" ? pastEventsLoading : upcomingEventsLoading;

  return (
    <div className="min-h-screen p-6 animate-fade-in">
      <div className="max-w-7xl mx-auto">
        <header className="dot-grid -mx-6 mb-8 flex flex-wrap items-center justify-between gap-4 px-6 py-6">
          <div className="min-w-0">
            <h1 className="text-4xl font-bold text-foreground">
              Casino Party Schedule
            </h1>
            <p className="mt-2 text-muted-foreground">
              Browse and register for upcoming events
            </p>
          </div>

          {/* The one element on the page allowed to move: it exists to keep
              learning a new position in mind. */}
          {showPositionButton && (
            <Button
              variant="cta"
              onClick={() => setPositionModalOpen(true)}
              className="group inline-flex shrink-0 items-center gap-3"
            >
              <span className="rounded-full bg-primary-foreground/15 p-1.5">
                <GraduationCap className="h-4 w-4" />
              </span>
              Want to learn more positions?
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </Button>
          )}
        </header>

        {/* Active Announcements and Bonus Program Section */}
        <div className="space-y-6 mb-8">
          {announcementLoading ? (
            <div className="h-40 flex items-center justify-center">
              <Loading text="Loading Announcements..." />
            </div>
          ) : (
            <AnnouncementsSection announcements={announcements || []} />
          )}
        </div>

        <div className="space-y-6 mb-8">
          {bonusLoading ? (
            <div className="h-40 flex items-center justify-center">
              <Loading text="Loading Bonuses..." />
            </div>
          ) : (
            <BonusProgramsSection programs={bonuses || []} />
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            {/* p-0 so a row can paint its own hover edge to edge. */}
            <Card className="overflow-hidden p-0">
              <div className="border-b border-border p-4">
                <FilterTabs
                  tabs={FILTER_TABS}
                  active={FILTERS.indexOf(filter)}
                  onChange={(i) => setFilter(FILTERS[i])}
                />
              </div>
              {/* The calendar is in the next column, and far below the fold
                  on a phone, so the filter it applies needs saying here. */}
              {date && (
                <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border bg-primary/[0.06] px-4 py-2.5">
                  <span className="flex items-center gap-2 text-sm text-foreground">
                    <CalendarDays className="h-4 w-4 text-primary" />
                    Showing {format(date, "EEEE, MMMM d")}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 gap-1.5 text-muted-foreground hover:text-foreground"
                    onClick={() => setDate(undefined)}
                  >
                    <X className="h-3.5 w-3.5" />
                    Clear
                  </Button>
                </div>
              )}
              <div className="divide-y divide-border">
                {isLoading ? (
                  <div className="h-40 flex items-center justify-center">
                    <Loading text="Loading Events..." />
                  </div>
                ) : (
                  <>
                    {visibleEvents.map((event) => (
                      <EventCard key={event.eventId} event={event} />
                    ))}
                    {visibleEvents.length === 0 &&
                      // "No upcoming events" would be a lie when a day filter
                      // is what emptied the list.
                      (date ? (
                        <EmptyState
                          icon={CalendarX}
                          title="No events on this day"
                          description={`Nothing is scheduled for ${format(date, "EEEE, MMMM d")}. The marked days on the calendar are the ones with events.`}
                          action={
                            <Button
                              variant="outline"
                              className="gap-2"
                              onClick={() => setDate(undefined)}
                            >
                              <CalendarDays className="h-4 w-4" />
                              Show every date
                            </Button>
                          }
                        />
                      ) : (
                        <EmptyState
                          icon={EMPTY[filter].icon}
                          title={EMPTY[filter].title}
                          description={EMPTY[filter].description}
                          action={
                            filter === "all" ? undefined : (
                              <Button
                                variant="outline"
                                className="gap-2"
                                onClick={() => setFilter("all")}
                              >
                                <Search className="h-4 w-4" />
                                Browse upcoming events
                              </Button>
                            )
                          }
                        />
                      ))}
                  </>
                )}
              </div>
            </Card>
          </div>

          <div className="lg:col-span-1">
            <EventCalendar
              date={date}
              setDate={setDate}
              events={upcomingEvents || []}
            />
          </div>
        </div>
      </div>

      {/* Position Request Modal */}
      <PositionRequestModal
        open={positionModalOpen}
        onOpenChange={setPositionModalOpen}
        availablePositions={availablePositions}
      />
    </div>
  );
};

export default Index;
